import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from './api/client';
import * as XLSX from 'xlsx';
import Icons from './components/icons';
import HeaderLogged from './components/header_logged';
import SmallBagsDailyPlan from './components/SmallBagsDailyPlan';
import SmallBagsViewSelector from './components/SmallBagsViewSelector';
import PacketsNeedTab from './components/PacketsNeedTab';
import './components/SmallBagsViewSelector.css';

const TAB_KEY = 'dp_tab';
const TAB_PARAM = { small_bags: 'small-bags', packets_need: 'packets-need' };   // вкладка → значение ?tab= в адресе
const tabOf = (param) => Object.keys(TAB_PARAM).find((t) => TAB_PARAM[t] === param);
const savedTab = () => { try { return localStorage.getItem(TAB_KEY) === 'packets_need' ? 'packets_need' : 'small_bags'; } catch { return 'small_bags'; } };

const DailyPlanning = () => {
  // Открытая вкладка живёт в адресе (/daily-planning?tab=packets-need): ссылка открывает ту же вкладку,
  // «Назад» возвращает предыдущую. Без параметра — вкладка, открытая в прошлый раз; она дописывается в адрес.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = tabOf(tabParam) || savedTab(); // 'small_bags' | 'packets_need'
  // Small Bags грузится десятки секунд (N+1 запросов): монтируем при первом открытии вкладки и дальше только прячем
  const [smallBagsSeen, setSmallBagsSeen] = useState(activeTab === 'small_bags');
  const setTabParam = (tab, replace) => setSearchParams((p) => { p.set('tab', TAB_PARAM[tab]); return p; }, { replace });
  const openTab = (tab) => { if (tab !== activeTab) setTabParam(tab, false); };
  useEffect(() => {
    if (activeTab === 'small_bags') setSmallBagsSeen(true);
    try { localStorage.setItem(TAB_KEY, activeTab); } catch {}
    if (!tabOf(tabParam)) setTabParam(activeTab, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tabParam]);
  const [smallBagsSearch, setSmallBagsSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const smallBagsRefreshRef = useRef(null);
  const [viewMode, setViewMode] = useState('all');

  // Filter state
  const [daysToShow, setDaysToShow] = useState(30);
  const [rowFilterRange, setRowFilterRange] = useState(null);
  const [selectedSets, setSelectedSets] = useState([]);
  const [demandForSelectedSets, setDemandForSelectedSets] = useState(false);
  const [excludePlanned, setExcludePlanned] = useState(false);
  const [showOnlyPlanned, setShowOnlyPlanned] = useState(false);
  const [bagShortageDays, setBagShortageDays] = useState(null);
  const [seedsShortageDays, setSeedsShortageDays] = useState(null);
  const [hideNonSetPackets, setHideNonSetPackets] = useState(true);
  const [sets, setSets] = useState([]);

  // Load sets
  useEffect(() => {
    const fetchSets = async () => {
      try {
        const response = await api.get('/api/sets');
        setSets(response.data.items || []);
      } catch (error) {
        console.error('Error fetching sets:', error);
        alert('Failed to load sets.');
      }
    };
    fetchSets();
  }, []);

  // Apply filters
  const applyFilters = () => {
    if (activeTab === 'small_bags' && smallBagsRefreshRef.current && smallBagsRefreshRef.current.applyFilters) {
      smallBagsRefreshRef.current.applyFilters({
        daysToShow,
        rowFilterRange,
        selectedSets,
        demandForSelectedSets,
        excludePlanned,
        showOnlyPlanned,
        bagShortageDays,
        seedsShortageDays,
        hideNonSetPackets,
      });
    }
  };

  // Reset filters
  const resetFilters = () => {
    setDaysToShow(30);
    setRowFilterRange(null);
    setSelectedSets([]);
    setDemandForSelectedSets(false);
    setExcludePlanned(false);
    setShowOnlyPlanned(false);
    setBagShortageDays(null);
    setSeedsShortageDays(null);
    setHideNonSetPackets(true);
    applyFilters();
  };

  const handleRefreshData = async () => {
    setIsLoading(true);
    try {
      // 08.09.2026: no Google-Sheets sync here any more (it wipes masterbags_dailyplan edited on the Production Plan page)
      if (smallBagsRefreshRef.current && smallBagsRefreshRef.current.handleRefresh) {
        await smallBagsRefreshRef.current.handleRefresh();
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
      alert('Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFilters = () => setShowFilters(!showFilters);

  // Handle set selection
  const handleSetSelect = (e) => {
    const selectedSet = e.target.value;
    if (selectedSet && !selectedSets.includes(selectedSet)) {
      const newSelectedSets = [...selectedSets, selectedSet];
      setSelectedSets(newSelectedSets);
      if (activeTab === 'small_bags') {
        smallBagsRefreshRef.current?.applyFilters({
          daysToShow,
          rowFilterRange,
          selectedSets: newSelectedSets,
          demandForSelectedSets,
          excludePlanned,
          showOnlyPlanned,
          bagShortageDays,
          seedsShortageDays,
          hideNonSetPackets,
        });
      }
    }
    e.target.value = '';
  };

  // Handle set removal
  const handleRemoveSet = (setSKU) => {
    const newSelectedSets = selectedSets.filter((sku) => sku !== setSKU);
    setSelectedSets(newSelectedSets);
    if (activeTab === 'small_bags') {
      smallBagsRefreshRef.current?.applyFilters({
        daysToShow,
        rowFilterRange,
        selectedSets: newSelectedSets,
        demandForSelectedSets,
        excludePlanned,
        showOnlyPlanned,
        bagShortageDays,
        seedsShortageDays,
        hideNonSetPackets,
      });
    }
  };

  const handleDownloadExcel = () => {
  if (!smallBagsRefreshRef.current || !smallBagsRefreshRef.current.getTableData) {
    alert('Table data not available');
    return;
  }

  const {
    filteredDataRows,
    dateColumns,
    totalDemand,
    plannedProduction,
    productionStatus,
    dailyTotals,
    isTotalDemandCollapsed,
    filters,
  } = smallBagsRefreshRef.current.getTableData();

  // Prepare headers
  const headers = [
    'Name',
    'Stock',
    'Demand Stock',
    ...(isTotalDemandCollapsed ? [] : ['Total Demand']),
    ...dateColumns.flatMap(col => {
      const { totalQty = 0, skuCount = 0 } = dailyTotals[col.dateStr] || {};
      const dateLabel = `${col.date.getMonth() + 1}/${col.date.getDate()}/${col.date.getFullYear()} (Total: ${totalQty.toLocaleString()}/${skuCount})`;
      return viewMode === 'all'
        ? [dateLabel, 'PP']
        : [dateLabel];
    }),
  ];

  // Prepare subheaders for date columns
  const subHeaders = [
    '', // Name
    '', // Stock
    '7d / 14d / 30d / 90d / 180d / 365d', // Demand Stock
    ...(isTotalDemandCollapsed ? [] : ['7d / 14d / 30d / 90d / 180d / 365d']), // Total Demand
    ...dateColumns.flatMap(() => (
      viewMode === 'all'
        ? ['Demand / Left / Left+PP', '']
        : [viewMode === 'demand' ? 'Demand' : viewMode === 'left' ? 'Left' : 'Left + PP']
    )),
  ];

  // Prepare data rows
  const data = filteredDataRows.map(row => {
    const leftValues = calculateRollingLeft(row.quantities, row.stock);
    const leftPlusPPValues = calculateLeftPlusPP(leftValues, row.SKU, dateColumns, plannedProduction, productionStatus);
    const totalStock = Math.max(row.stock, 0) + row.canProduce;
    const demands = totalDemand[row.SKU] || [0, 0, 0, 0, 0, 0];
    const endStockCol = dateColumns.find(col => leftValues[col.qIdx] < 0);
    const endStockDate = endStockCol ? endStockCol.date : null;

    const plannedDetails = dateColumns
      .map((col, colIndex) => {
        const planKey = `${row.SKU}_${col.dateStr}`;
        const plannedQty = plannedProduction[planKey]?.qty || 0;
        const status = productionStatus[planKey] ?? 'Planned';
        if (plannedQty > 0) {
          return `${status}: ${plannedQty.toLocaleString()}, Start Date: ${col.date.toLocaleDateString()}`;
        }
        return null;
      })
      .filter(detail => detail !== null);

    const rowData = [
      `${row.name}${plannedDetails.length > 0 ? '\n' + plannedDetails.join('\n') : ''}`,
      `Stock: ${Math.max(row.stock, 0).toLocaleString()}\nCBP: ${row.canProduce.toLocaleString()}\nTotal: ${totalStock.toLocaleString()}\nEnd date: ${endStockDate ? endStockDate.toLocaleDateString() : ''}`,
      demands.map((d, i) => `${[7, 14, 30, 90, 180, 365][i]}d: ${d.toLocaleString()}`).join('\n'),
      ...(isTotalDemandCollapsed ? [] : [demands.map((d, i) => `${[7, 14, 30, 90, 180, 365][i]}d: ${d.toLocaleString()}`).join('\n')]),
      ...dateColumns.flatMap((col, colIndex) => {
        const planKey = `${row.SKU}_${col.dateStr}`;
        const plannedQty = plannedProduction[planKey]?.qty || 0;
        const L = leftValues[col.qIdx];
        const LPP = leftPlusPPValues[col.qIdx];
        const K_L = findCoverageDays(row.quantities, col.qIdx, L);
        const K_LPP = findCoverageDays(row.quantities, col.qIdx, LPP);

        if (viewMode === 'all') {
          return [
            `D: ${row.quantities[col.qIdx].toLocaleString()}\nL: ${L.toLocaleString()}\nL+PP: ${LPP.toLocaleString()}`,
            plannedQty > 0 ? plannedQty.toLocaleString() : '',
          ];
        } else if (viewMode === 'demand') {
          return [`D: ${row.quantities[col.qIdx].toLocaleString()}`];
        } else if (viewMode === 'left') {
          return [`Լ: ${L.toLocaleString()}`];
        } else {
          return [`L+PP: ${LPP.toLocaleString()}`];
        }
      }),
    ];

    return rowData;
  });

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet([headers, subHeaders, ...data]);

  // Apply basic formatting
  ws['!cols'] = headers.map((_, i) => ({
    wch: i < 2 ? 30 : i < (isTotalDemandCollapsed ? 3 : 4) ? 20 : 15,
  }));

  // Apply bold to headers
  headers.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: i });
    if (!ws[cell]) ws[cell] = {};
    ws[cell].s = { font: { bold: true } };
  });

  // Apply colors to demand columns
  data.forEach((row, rowIndex) => {
    const demands = totalDemand[filteredDataRows[rowIndex].SKU] || [0, 0, 0, 0, 0, 0];
    const totalStock = Math.max(filteredDataRows[rowIndex].stock, 0) + filteredDataRows[rowIndex].canProduce;

    [2, ...(isTotalDemandCollapsed ? [] : [3])].forEach(colIndex => {
      const cell = XLSX.utils.encode_cell({ r: rowIndex + 2, c: colIndex });
      if (!ws[cell]) ws[cell] = {};
      ws[cell].s = {
        fill: {
          fgColor: { rgb: demands.some(d => (colIndex === 2 ? Math.max(filteredDataRows[rowIndex].stock, 0) : totalStock) <= d) ? 'FF0000' : '00FF00' },
        },
      };
    });

    dateColumns.forEach((col, colIndex) => {
      const L = calculateRollingLeft(filteredDataRows[rowIndex].quantities, filteredDataRows[rowIndex].stock)[col.qIdx];
      const LPP = calculateLeftPlusPP(
        calculateRollingLeft(filteredDataRows[rowIndex].quantities, filteredDataRows[rowIndex].stock),
        filteredDataRows[rowIndex].SKU,
        dateColumns,
        plannedProduction,
        productionStatus
      )[col.qIdx];
      const K_L = findCoverageDays(filteredDataRows[rowIndex].quantities, col.qIdx, L);
      const K_LPP = findCoverageDays(filteredDataRows[rowIndex].quantities, col.qIdx, LPP);

      const getColorClass = (K) => {
        if (K >= 3 || K === Infinity) return '00FF00'; // Green
        if (K >= 1) return 'FFFF00'; // Yellow
        return 'FF0000'; // Red
      };

      const baseCol = (isTotalDemandCollapsed ? 3 : 4) + colIndex * (viewMode === 'all' ? 2 : 1);
      if (viewMode === 'all' || viewMode === 'left') {
        const cell = XLSX.utils.encode_cell({ r: rowIndex + 2, c: baseCol });
        if (!ws[cell]) ws[cell] = {};
        ws[cell].s = { fill: { fgColor: { rgb: getColorClass(K_L) } } };
      }
      if (viewMode === 'all' || viewMode === 'left_pp') {
        const cell = XLSX.utils.encode_cell({ r: rowIndex + 2, c: baseCol + (viewMode === 'all' ? 0 : 0) });
        if (!ws[cell]) ws[cell] = {};
        ws[cell].s = { fill: { fgColor: { rgb: getColorClass(K_LPP) } } };
      }
    });
  });

  // Create workbook and download
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Small Bags Plan');
  XLSX.writeFile(wb, `SmallBagsDailyPlan_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// Helper functions for Excel generation
const calculateRollingLeft = (quantities, stock) => {
  const leftValues = [];
  let cumulativeUsed = 0;
  quantities.forEach(qty => {
    cumulativeUsed += Number(qty) || 0;
    const left = Math.max(Number(stock), 0) - cumulativeUsed;
    leftValues.push(left);
  });
  return leftValues;
};

const calculateLeftPlusPP = (leftValues, sku, dateColumns, plannedProduction, productionStatus) => {
  const lpp = [...leftValues];
  let cumulativePlanned = 0;
  dateColumns.forEach(col => {
    const planKey = `${sku}_${col.dateStr}`;
    const status = productionStatus[planKey] || 'Planned';
    if (status !== 'Complete') cumulativePlanned += Number(plannedProduction[planKey]?.qty || 0);
    lpp[col.qIdx] = leftValues[col.qIdx] + cumulativePlanned;
  });
  return lpp;
};

const findCoverageDays = (quantities, startIndex, S) => {
  if (S < 0) return 0;
  let cumulative = 0;
  for (let K = 0; K < quantities.length - startIndex - 1; K++) {
    cumulative += quantities[startIndex + 1 + K];
    if (cumulative > S) {
      return K;
    }
  }
  return Infinity;
};

  return (
    <>
      <Icons />
      <HeaderLogged />
      <div className="container-big">
        <div className="pn-tabs">
          <button className={`pp-tgl${activeTab === 'small_bags' ? ' on' : ''}`} onClick={() => openTab('small_bags')}>Small Bags</button>
          <button className={`pp-tgl${activeTab === 'packets_need' ? ' on' : ''}`} onClick={() => openTab('packets_need')}>Packets Need</button>
        </div>
        {activeTab === 'packets_need' && <PacketsNeedTab />}
        <div className="row space__between buttons_and_filters_planing" style={activeTab === 'small_bags' ? undefined : { display: 'none' }}>
          <div className="section__search_and_filter">
            {activeTab === 'small_bags' && (
              <input
                type="search"
                className="table__search"
                placeholder="Search by name or SKU..."
                value={smallBagsSearch}
                onChange={(e) => setSmallBagsSearch(e.target.value)}
              />
            )}
          </div>
          <div className="section__buttons">
            <div className="filter-container">
              <button
                className="btn btn__blue mr-5"
                onClick={toggleFilters}
                disabled={isLoading}
              >
                Filters
              </button>
              {activeTab === 'small_bags' && (
                <>
                  <SmallBagsViewSelector
                    onViewChange={setViewMode}
                    currentView={viewMode}
                  />
                  <button
                    className="btn btn__green mr-5"
                    onClick={handleDownloadExcel}
                    disabled={isLoading}
                  >
                    Download Excel
                  </button>
                </>
              )}
              {showFilters && (
                <div className="filter-controls">
                  <h6>Filter Options</h6>
                  <div>
                    <label>Days to Show:</label>
                    <select
                      value={daysToShow}
                      onChange={(e) => {
                        const newValue = Number(e.target.value);
                        setDaysToShow(newValue);
                        if (activeTab === 'small_bags') {
                          smallBagsRefreshRef.current?.applyFilters({
                            daysToShow: newValue,
                            rowFilterRange,
                            selectedSets,
                            demandForSelectedSets,
                            excludePlanned,
                            showOnlyPlanned,
                            bagShortageDays,
                            seedsShortageDays,
                            hideNonSetPackets,
                          });
                        }
                      }}
                    >
                      <option value="" disabled>
                        Select days
                      </option>
                      {[30, 60, 90, 180, 365].map((days) => (
                        <option key={days} value={days}>
                          {days} days
                        </option>
                      ))}
                    </select>
                  </div>
                  {activeTab === 'small_bags' && (
                    <>
                      <div>
                        <label>Demand Range:</label>
                        <select
                          value={rowFilterRange || ''}
                          onChange={(e) => {
                            const newValue = e.target.value ? Number(e.target.value) : null;
                            setRowFilterRange(newValue);
                            smallBagsRefreshRef.current?.applyFilters({
                              daysToShow,
                              rowFilterRange: newValue,
                              selectedSets,
                              demandForSelectedSets,
                              excludePlanned,
                              showOnlyPlanned,
                              bagShortageDays,
                              seedsShortageDays,
                              hideNonSetPackets,
                            });
                          }}
                        >
                          <option value="">None</option>
                          {[1, 3, 7, 14, 30, 90, 180, 365].map((range) => (
                            <option key={range} value={range}>
                              {range} days
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label>Select Sets:</label>
                        <select onChange={handleSetSelect}>
                          <option value="">Select a set</option>
                          {sets
                            .filter((set) => !selectedSets.includes(set.SKU))
                            .map((set) => (
                              <option key={set.SKU} value={set.SKU}>
                                {set.setmane}
                              </option>
                            ))}
                        </select>
                        <div className="selected-sets-tags">
                          {selectedSets.map((setSKU) => {
                            const set = sets.find((s) => s.SKU === setSKU);
                            return (
                              <span key={setSKU} className="set-tag">
                                {set ? set.setmane : setSKU}
                                <span
                                  className="remove-tag"
                                  onClick={() => handleRemoveSet(setSKU)}
                                >
                                  ✕
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label>
                          <input
                            type="checkbox"
                            checked={demandForSelectedSets}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setDemandForSelectedSets(newValue);
                              smallBagsRefreshRef.current?.applyFilters({
                                daysToShow,
                                rowFilterRange,
                                selectedSets,
                                demandForSelectedSets: newValue,
                                excludePlanned,
                                showOnlyPlanned,
                                bagShortageDays,
                                seedsShortageDays,
                                hideNonSetPackets,
                              });
                            }}
                          />
                          Show demand only for selected sets
                        </label>
                      </div>
                      <div>
                        <label>
                          <input
                            type="checkbox"
                            checked={excludePlanned}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setExcludePlanned(newValue);
                              smallBagsRefreshRef.current?.applyFilters({
                                daysToShow,
                                rowFilterRange,
                                selectedSets,
                                demandForSelectedSets,
                                excludePlanned: newValue,
                                showOnlyPlanned,
                                bagShortageDays,
                                seedsShortageDays,
                                hideNonSetPackets,
                              });
                            }}
                          />
                          Exclude SKUs in Plan/In Production
                        </label>
                      </div>
                      <div>
                        <label>
                          <input
                            type="checkbox"
                            checked={showOnlyPlanned}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setShowOnlyPlanned(newValue);
                              smallBagsRefreshRef.current?.applyFilters({
                                daysToShow,
                                rowFilterRange,
                                selectedSets,
                                demandForSelectedSets,
                                excludePlanned,
                                showOnlyPlanned: newValue,
                                bagShortageDays,
                                seedsShortageDays,
                                hideNonSetPackets,
                              });
                            }}
                          />
                          Show only Planned/In Production
                        </label>
                      </div>
                      <div>
                        <label>
                          <input
                            type="checkbox"
                            checked={hideNonSetPackets}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setHideNonSetPackets(newValue);
                              smallBagsRefreshRef.current?.applyFilters({
                                daysToShow,
                                rowFilterRange,
                                selectedSets,
                                demandForSelectedSets,
                                excludePlanned,
                                showOnlyPlanned,
                                bagShortageDays,
                                seedsShortageDays,
                                hideNonSetPackets: newValue,
                              });
                            }}
                          />
                          Hide packets not in any set
                        </label>
                      </div>
                      <div>
                        <label>Show Bag Shortage:</label>
                        <select
                          value={bagShortageDays || ''}
                          onChange={(e) => {
                            const newValue = e.target.value ? Number(e.target.value) : null;
                            setBagShortageDays(newValue);
                            smallBagsRefreshRef.current?.applyFilters({
                              daysToShow,
                              rowFilterRange,
                              selectedSets,
                              demandForSelectedSets,
                              excludePlanned,
                              showOnlyPlanned,
                              bagShortageDays: newValue,
                              seedsShortageDays,
                              hideNonSetPackets,
                            });
                          }}
                        >
                          <option value="">None</option>
                          {[7, 14, 30, 90, 180, 365].map((range) => (
                            <option key={range} value={range}>
                              {range} days
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label>Show Seeds Shortage:</label>
                        <select
                          value={seedsShortageDays || ''}
                          onChange={(e) => {
                            const newValue = e.target.value ? Number(e.target.value) : null;
                            setSeedsShortageDays(newValue);
                            smallBagsRefreshRef.current?.applyFilters({
                              daysToShow,
                              rowFilterRange,
                              selectedSets,
                              demandForSelectedSets,
                              excludePlanned,
                              showOnlyPlanned,
                              bagShortageDays,
                              seedsShortageDays: newValue,
                              hideNonSetPackets,
                            });
                          }}
                        >
                          <option value="">None</option>
                          {[7, 14, 30, 90, 180, 365].map((range) => (
                            <option key={range} value={range}>
                              {range} days
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  <div>
                    <button
                      className="reset-button"
                      onClick={resetFilters}
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              className="btn btn__green mr-5"
              onClick={handleRefreshData}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Refresh Data'}
            </button>
          </div>
        </div>
        {/* Вкладка 'daily_plan' (DailyPlanTab, живой вид Google-листа) заменена страницей Production Plan; компонент остался в репо.
            29.09.2026: вторая вкладка Packets Need (PacketsNeedTab) — со своим тулбаром, рендерится выше. */}
        <div className="tabs-container material-order-tabs" style={activeTab === 'small_bags' ? undefined : { display: 'none' }}>
          <div className="tab-content">
            <div className="tabcontent active">
              {smallBagsSeen && (
                <SmallBagsDailyPlan
                  ref={smallBagsRefreshRef}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  searchTerm={smallBagsSearch}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DailyPlanning;